import { motion, AnimatePresence } from "framer-motion";
import { useShallow } from "zustand/react/shallow";
import Button from "../ui/Button";
import { useLanguage } from "../../i18n/LanguageContext";
import type { Player } from "../../types/game";
import { useGameStore, selectAvailableItems } from "../../store/gameStore";

interface Props { player: Player; }

export default function SpendPhase({ player }: Props) {
  const { phase, costOfLiving, dispatch } = useGameStore();
  const availableItems = useGameStore(useShallow(selectAvailableItems));
  const { t } = useLanguage();

  if (phase === "SPEND") {
    return (
      <motion.div className="space-y-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
          <div className="text-red-400 font-bold text-lg font-display">{t.spend.costOfLivingTitle}</div>
          <div className="text-white text-3xl font-black font-display mt-1">🪙{costOfLiving}</div>
          <div className="text-white/50 text-sm mt-1">{t.spend.costOfLivingNote}</div>
          <div className="text-white/40 text-xs mt-1">{t.spend.yourBalance(player.cash, player.cash - costOfLiving)}</div>
        </div>
        <Button onClick={() => dispatch({ type: "CONFIRM_SPEND" })} size="lg" className="w-full">
          {t.spend.payAndContinue(costOfLiving)}
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div className="space-y-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-white text-base">{t.spend.buyItemsTitle}</div>
        <motion.div
          className="text-xs bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 text-yellow-300 px-2 py-1 rounded-full"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          {t.spend.wlbBoostHint}
        </motion.div>
      </div>

      {availableItems.length === 0 ? (
        <div className="text-center text-white/30 py-4 text-sm">{t.spend.cantAfford}</div>
      ) : (
        <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
          <AnimatePresence>
            {availableItems.map((item, i) => (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                onClick={() => dispatch({ type: "BUY_ITEM", itemId: item.id })}
                className="w-full relative overflow-hidden bg-gradient-to-r from-slate-700/60 to-slate-800/60 hover:from-slate-700 hover:to-slate-700/80 border border-white/10 hover:border-yellow-400/40 rounded-xl p-3 text-left transition-colors flex items-center gap-3 group shadow-sm"
              >
                {/* Subtle shimmer line on hover */}
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-transparent via-white/5 to-transparent" />

                <span className="text-3xl shrink-0 group-hover:scale-110 transition-transform duration-200 inline-block drop-shadow-sm">
                  {item.emoji}
                </span>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-1.5 leading-tight flex-wrap">
                    <span className="font-bold text-white text-sm">{t.items?.[item.id]?.name ?? item.name}</span>
                    {item.bestseller && (
                      <span className="text-xs font-black bg-gradient-to-r from-orange-500 to-pink-500 text-white px-1.5 py-0.5 rounded-full leading-none shrink-0">{t.spend.popularBadge}</span>
                    )}
                  </div>
                  <div className="text-xs text-white/45 mt-0.5 leading-snug">{t.items?.[item.id]?.description ?? item.description}</div>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {item.wlbBonus > 0 && (
                      <span className="text-xs font-semibold bg-green-500/15 text-green-400 px-1.5 py-0.5 rounded-full">
                        {t.spend.wlbBonus(item.wlbBonus)} ❤️
                      </span>
                    )}
                    {item.resaleValue > 0 && (
                      <span className="text-xs text-white/30 bg-white/5 px-1.5 py-0.5 rounded-full">
                        {t.spend.resell(item.resaleValue)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="shrink-0 text-right">
                  <div className="font-black text-base text-yellow-300">🪙{item.cost}</div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Owned items — sell section */}
      {player.ownedItems.filter((i) => i.resaleValue > 0).length > 0 && (
        <div className="border-t border-white/8 pt-3 space-y-1.5">
          <div className="text-xs text-white/40 font-semibold uppercase tracking-wide">{t.spend.sellBackTitle}</div>
          {player.ownedItems.filter((i) => i.resaleValue > 0).map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              className="flex items-center gap-2 bg-white/4 border border-white/8 rounded-xl px-3 py-2"
            >
              <span className="text-xl">{item.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white/80">{t.items?.[item.id]?.name ?? item.name}</div>
                {item.wlbBonus > 0 && (
                  <div className="text-xs text-orange-400/70">{t.spend.sellingRemovesWlb(item.wlbBonus)}</div>
                )}
              </div>
              <button
                onClick={() => dispatch({ type: "SELL_ITEM", itemId: item.id })}
                className="text-xs font-bold text-yellow-300 hover:text-yellow-200 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/20 px-2.5 py-1 rounded-lg transition-all"
              >
                {t.spend.sellFor(item.resaleValue)}
              </button>
            </motion.div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <Button onClick={() => dispatch({ type: "END_TURN" })} size="lg" className="w-full">{t.spend.endTurn}</Button>
      </div>
    </motion.div>
  );
}
